import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link, animateScroll } from 'react-scroll';

import { NavigationContext } from 'buyside/components/presentation/MainSiteLayout/mainSiteLayout';
import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';

import styles from './pageNavigatorStyles.scss';

const PageNavigator: FC<{
  fullWidth?: boolean;
  items: {
    label: string;
    // 'id' represents the id of the element we want to scroll to
    id: string;
  }[];
  slideIntoView?: boolean;
  // how far down the page (in pixels) for the page navigator to slide into view
  slideIntoViewOffset?: number;
}> = ({ fullWidth, items, slideIntoView, slideIntoViewOffset = 0 }) => {
  const { sideNavigationOpen } = useContext(NavigationContext) || {};

  const ref = useRef<HTMLDivElement>(null);

  const [activeItem, setActiveItem] = useState('');
  const [isVisible, setIsVisible] = useState(!slideIntoView);

  const handleScroll = useCallback((): void => {
    if (slideIntoView) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > slideIntoViewOffset);
    }
  }, [slideIntoView, slideIntoViewOffset]);

  useEffect(() => {
    if (ref.current && ref.current.scrollTo)
      ref.current.scrollTo({
        left: document.getElementById(activeItem)?.offsetLeft || 0,
        behavior: 'smooth'
      });
  }, [activeItem]);

  useEffect(() => {
    if (!slideIntoView) return;

    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, slideIntoView]);

  return (
    <div
      ref={ref}
      className={classNames(styles.pageNavigatorContainer, {
        [styles.fullWidth]: Boolean(fullWidth),
        [styles.slideIntoView]: slideIntoView,
        [styles.visible]: isVisible,
        [styles.sideNavigationOpen]: sideNavigationOpen
      })}
    >
      <div className={styles.pageNavigator}>
        {items.map(item => (
          <Link
            activeClass={styles.activeNavigatorLink}
            className={styles.navigatorLink}
            id={`${item.id}_button`}
            isDynamic
            key={item.id}
            offset={-(65 + 45 + 15)} // -(top navigation height + page navigator height + some padding)
            onSetActive={(item): void => setActiveItem(`${item}_button`)}
            smooth
            duration={500}
            spy
            to={item.id}
          >
            {item.label}
          </Link>
        ))}
      </div>
      <Button
        className={styles.navigatorLink}
        type={BUTTON_TYPES.LINK}
        text="Back To Top"
        onClick={() => animateScroll.scrollToTop({ duration: 500 })}
      />
    </div>
  );
};

export default PageNavigator;
